import React, { useMemo, useEffect, useState } from 'react';
import './HeadlineItem.scss';

export default function HeadlineItem({
  title,
  desc,
  username,
  avatarUrl,
  authorId,
  progressItems,
  viewedCount: extViewedCount,
  onClick,
  isPromoted,
  mode,
  fallbackAvatar,
  showAvatar = true,
  plusCentered = false,
  hasStories = true,
  isOnline = false,
  newListingsCount = 0,
}) {
  const name = username || 'user';
  const shortName = name.length > 16 ? name.slice(0, 14) + '…' : name;

  const resolvedAvatar = useMemo(() => {
    return !showAvatar
      ? ''
      : (avatarUrl && avatarUrl !== 'null' && avatarUrl !== 'undefined'
        ? avatarUrl
        : (fallbackAvatar || ''));
  }, [avatarUrl, fallbackAvatar, showAvatar]);

  const segCount = Array.isArray(progressItems) && progressItems.length > 0
    ? Math.min(10, Math.max(1, progressItems.length))
    : 1;

  const { lsViewedCount, lsCompletedAt } = useMemo(() => {
    if (!authorId) return { lsViewedCount: 0, lsCompletedAt: 0 };
    try {
      const raw = localStorage.getItem(`hl_seen_author_${authorId}`);
      if (!raw) return { lsViewedCount: 0, lsCompletedAt: 0 };
      const obj = JSON.parse(raw);
      const v = Number(obj?.viewedCount ?? 0);
      const c = Number(obj?.completedAt ?? 0);
      return {
        lsViewedCount: Number.isFinite(v) && v >= 0 ? v : 0,
        lsCompletedAt: Number.isFinite(c) && c > 0 ? c : 0,
      };
    } catch {
      return { lsViewedCount: 0, lsCompletedAt: 0 };
    }
  }, [authorId]);

  const [runtimeSeen, setRuntimeSeen] = useState(() => ({
    viewedCount: lsViewedCount,
    completedAt: lsCompletedAt,
  }));

  useEffect(() => {
    setRuntimeSeen({
      viewedCount: lsViewedCount,
      completedAt: lsCompletedAt,
    });
  }, [lsViewedCount, lsCompletedAt]);

  useEffect(() => {
    if (!authorId || typeof window === 'undefined') return;

    const handler = (e) => {
      try {
        const detail = e?.detail || {};
        if (!detail || detail.authorId !== authorId) return;

        let nextViewed = runtimeSeen.viewedCount;
        let nextCompleted = runtimeSeen.completedAt;

        try {
          const raw = localStorage.getItem(`hl_seen_author_${authorId}`);
          if (raw) {
            const obj = JSON.parse(raw);
            const v = Number(obj?.viewedCount ?? 0);
            const c = Number(obj?.completedAt ?? 0);
            if (Number.isFinite(v) && v >= 0) nextViewed = v;
            if (Number.isFinite(c) && c > 0) nextCompleted = c;
          }
        } catch {
          const v = Number(detail.viewedCount);
          if (Number.isFinite(v) && v >= 0) {
            nextViewed = v;
          }
        }

        setRuntimeSeen({
          viewedCount: nextViewed,
          completedAt: nextCompleted,
        });
      } catch {
      }
    };

    window.addEventListener('cvanto_headline_seen', handler);
    return () => {
      window.removeEventListener('cvanto_headline_seen', handler);
    };
  }, [authorId, runtimeSeen.viewedCount, runtimeSeen.completedAt]);

  const countAtCompleted = useMemo(() => {
    if (!Array.isArray(progressItems) || progressItems.length === 0 || !runtimeSeen.completedAt) return Infinity;
    return progressItems.filter(it => Number(it?.createdAt || 0) <= runtimeSeen.completedAt).length || 0;
  }, [progressItems, runtimeSeen.completedAt]);

  const baseViewed = useMemo(() => {
    const fromProp = (typeof extViewedCount === 'number') ? extViewedCount : null;
    if (fromProp !== null) return Math.max(0, Math.min(segCount, fromProp));
    return Math.max(0, Math.min(segCount, runtimeSeen.viewedCount));
  }, [extViewedCount, segCount, runtimeSeen.viewedCount]);

  const viewedCount = Math.max(0, Math.min(segCount, Math.min(baseViewed, countAtCompleted)));
  const hasUnviewed = hasStories && viewedCount < segCount;

  const isCreate = mode === 'create';
  const hasCreateAvatar = isCreate && !!resolvedAvatar;
  const isCreateGuest = isCreate && !resolvedAvatar;

  const rootClass = [
    'headline-item',
    isPromoted ? 'is-promoted' : '',
    isCreate ? 'is-create' : '',
    isCreateGuest ? 'no-avatar' : '',
    isCreateGuest ? 'is-create-guest' : '',
    hasCreateAvatar ? 'is-create-auth' : '',
    plusCentered && isCreateGuest ? 'plus-centered' : '',
    hasUnviewed ? 'is-fresh' : '',
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={rootClass}
      onClick={onClick}
      title={title}
      data-segments={segCount}
    >
            <div className="headline-item__circle">
        {!isCreate && hasStories && (
          // Visual refinement only (2026-09) — the old 4-layer conic-
          // gradient/segment/glow ring is replaced by one thin solid ring;
          // hasUnviewed/viewedCount/segCount (the real read/unread state,
          // computed above) are completely unchanged, this just renders
          // that state more simply.
          <div
            className={`headline-item__ring-simple ${hasUnviewed ? 'is-unviewed' : 'is-viewed'}`}
            aria-hidden="true"
          />
        )}

        {hasCreateAvatar ? (
          <img
            src={resolvedAvatar}
            alt={username || 'avatar'}
            className="headline-item__avatar"
            draggable={false}
            onError={(e) => {
              e.currentTarget.onerror = null;
              if (fallbackAvatar) e.currentTarget.src = fallbackAvatar;
            }}
          />
        ) : null}

        {isCreateGuest && (
          <span className="headline-item__createGuestRing" aria-hidden="true" />
        )}

                {(!isCreate || hasCreateAvatar) && !!resolvedAvatar && !hasCreateAvatar && (
          <img
            src={resolvedAvatar}
            alt={username || 'avatar'}
            className="headline-item__avatar"
            draggable={false}
            onError={(e) => {
              e.currentTarget.onerror = null;
              if (fallbackAvatar) e.currentTarget.src = fallbackAvatar;
            }}
          />
        )}

        {!isCreate && isOnline && (
          <span className="headline-item__onlineDot" aria-hidden="true" />
        )}

        {!isCreate && Number(newListingsCount) > 0 && (
          <span className="headline-item__newBadge" aria-hidden="true">+1</span>
        )}

        {isCreate && (
          <span className="headline-item__plus" aria-label="Створити новий Story">
            {hasCreateAvatar ? '+' : null}
          </span>
        )}

        {isPromoted && <span className="headline-item__badge">Boost</span>}
      </div>

      <div className={`headline-item__name ${mode === 'create' ? 'headline-item__name--create' : ''}`}>
        {mode === 'create' ? 'новий Story' : shortName}
      </div>

      {desc && <div className="headline-item__desc">{desc}</div>}
    </button>
  );
}
